// The collection screen's URL contract (Pass 2): which section is showing,
// which binder is open, and the open binder's page / search / layout / sort.
// Pure, no router or DOM: the route hook reads location and navigates, this
// file only parses and builds.
//
//   /collection                          Cards
//   /collection/binders                  the binder shelf
//   /collection/binders/:binderId        one binder (a positive int or `unsorted`)
//   /collection/sealed                   Sealed
//
// Binder params live only on the binder route:
//   ?page=  1-based page (1 is the default and is omitted)
//   ?q=     free text only (filter chips are not kept in the URL)
//   ?layout=pages|list
//   ?sort=<SortOptionId>
//   ?group=printing|name|none  the pockets (By printing, By card name, Each
//          copy); absent = the stored choice, else the size default
//
// Anything invalid is dropped, and `normalized` carries the URL to replace
// to. Query keys this file does not own are passed through untouched.
import { BINDER_VIEWS, SECTIONS, type BinderView, type Section } from '../api/collectionBinders.codes'
import { GROUP_MODES, SORT_OPTIONS, type GroupMode, type SortOptionId } from '../api/collectionCopies.codes'

export type CollectionSection = Section

/** The route segment of Unsorted (it has no binder id). */
export const BINDER_ROUTE_UNSORTED = 'unsorted'

/** A binder in the URL: a positive integer id as a string, or `unsorted`. */
export type BinderRouteRef = `${number}` | typeof BINDER_ROUTE_UNSORTED

export type BinderLayout = BinderView

export type BinderParams = {
  /** 1-based. */
  page: number
  /** Free text; '' when absent. */
  q: string
  /** null = no URL choice (the caller falls back to localStorage). */
  layout: BinderLayout | null
  /** null = the view's default sort. */
  sort: SortOptionId | null
  /** null = no URL choice (the stored choice, else the size default). `none` is explicit: Each copy. */
  group: GroupMode | null
}

export const COLLECTION_BASE = '/collection'

/** The query keys this contract owns. */
export const BINDER_PARAM_KEYS = ['page', 'q', 'layout', 'sort', 'group'] as const

const [PAGE, Q, LAYOUT, SORT, GROUP] = BINDER_PARAM_KEYS
// SECTIONS is the tuple cards, binders, sealed; the route tests pin that order.
const [CARDS, BINDERS, SEALED] = SECTIONS

export const DEFAULT_BINDER_PARAMS: Readonly<BinderParams> = Object.freeze({ page: 1, q: '', layout: null, sort: null, group: null })

export type CollectionLocation = {
  section: CollectionSection
  /** Set only on the binder route. */
  binder?: BinderRouteRef
  /** Always present; the defaults off the binder route. */
  params: BinderParams
  /** Set when the URL was not canonical: the path plus search to replace to. */
  normalized?: string
}

// Cards is the bare base; the other sections' segments are their ids.
const SECTION_SEGMENT: Record<CollectionSection, string | null> = {
  [CARDS]: null,
  [BINDERS]: BINDERS,
  [SEALED]: SEALED,
}

const SORT_IDS: ReadonlySet<string> = new Set(SORT_OPTIONS.map(o => o.id))
const LAYOUTS: ReadonlySet<string> = new Set(BINDER_VIEWS)
const GROUPS: ReadonlySet<string> = new Set(GROUP_MODES)

/** A positive integer written as digits, within the safe integer range. */
function positiveInt(raw: string): number | null {
  if (!/^\d+$/.test(raw)) return null
  const n = Number(raw)
  return Number.isSafeInteger(n) && n > 0 ? n : null
}

/**
 * The canonical route ref of a URL segment, or null. Leading zeros are
 * accepted and canonicalized ('012' -> '12'); the caller sees the change
 * through `normalized`.
 */
export function parseBinderRouteRef(raw: string): BinderRouteRef | null {
  if (raw === BINDER_ROUTE_UNSORTED) return BINDER_ROUTE_UNSORTED
  const n = positiveInt(raw)
  return n === null ? null : (String(n) as `${number}`)
}

/** The route ref of a binder's id (Unsorted has a null id). */
export function binderRouteRef(binderId: number | null): BinderRouteRef {
  return binderId === null ? BINDER_ROUTE_UNSORTED : (String(binderId) as `${number}`)
}

/** The binder id a route ref names (null = Unsorted). */
export function binderIdOfRef(ref: BinderRouteRef): number | null {
  return ref === BINDER_ROUTE_UNSORTED ? null : Number(ref)
}

function binderQuery(params: Partial<BinderParams> | undefined): URLSearchParams {
  const out = new URLSearchParams()
  if (!params) return out
  if (params.page !== undefined && Number.isSafeInteger(params.page) && params.page > 1) out.set(PAGE, String(params.page))
  if (params.q) out.set(Q, params.q)
  if (params.layout && LAYOUTS.has(params.layout)) out.set(LAYOUT, params.layout)
  if (params.sort && SORT_IDS.has(params.sort)) out.set(SORT, params.sort)
  if (params.group && GROUPS.has(params.group)) out.set(GROUP, params.group)
  return out
}

/**
 * The URL of a section, a binder, and the binder's params. Defaults are
 * omitted (page 1, empty q, null layout, sort and group). `binder` is used only
 * with the binders section, and `params` only with a binder.
 */
export function collectionPath(section: CollectionSection, binder?: BinderRouteRef | null, params?: Partial<BinderParams>): string {
  const seg = SECTION_SEGMENT[section] ?? null
  if (seg === null) return COLLECTION_BASE
  if (section !== BINDERS || !binder) return `${COLLECTION_BASE}/${seg}`
  const qs = binderQuery(params).toString()
  return `${COLLECTION_BASE}/${seg}/${binder}${qs ? `?${qs}` : ''}`
}

type Path = { section: CollectionSection; binder?: BinderRouteRef; canonical: boolean }

function parsePath(pathname: string): Path {
  const fallback: Path = { section: CARDS, canonical: false }
  if (pathname !== COLLECTION_BASE && !pathname.startsWith(`${COLLECTION_BASE}/`)) return fallback
  const rest = pathname.slice(COLLECTION_BASE.length)
  // One trailing slash is tolerated (and normalized away); empty segments
  // anywhere else are not a route.
  const trimmed = rest.endsWith('/') ? rest.slice(0, -1) : rest
  const slash = trimmed !== rest
  const segs = trimmed === '' ? [] : trimmed.slice(1).split('/')
  if (segs.some(s => s === '')) return fallback
  if (segs.length === 0) return { section: CARDS, canonical: !slash }
  const [first, second, ...more] = segs
  if (first === SECTION_SEGMENT.sealed && segs.length === 1) return { section: SEALED, canonical: !slash }
  if (first !== SECTION_SEGMENT.binders) return fallback
  if (second === undefined) return { section: BINDERS, canonical: !slash }
  const ref = more.length === 0 ? parseBinderRouteRef(second) : null
  // A bad binder segment lands on the shelf, not on Cards.
  if (ref === null) return { section: BINDERS, canonical: false }
  return { section: BINDERS, binder: ref, canonical: !slash && ref === second }
}

/**
 * Reads a collection URL. `search` may carry its leading '?'. Invalid or
 * default-valued binder params, duplicated keys, and binder params off the
 * binder route are dropped; any of those (or a non-canonical path) sets
 * `normalized`.
 */
export function parseCollectionLocation(pathname: string, search: string): CollectionLocation {
  const path = parsePath(pathname)
  const query = new URLSearchParams(search)
  const owned = new Set<string>(BINDER_PARAM_KEYS)
  const params: BinderParams = { ...DEFAULT_BINDER_PARAMS }
  let dirty = !path.canonical

  if (path.binder) {
    for (const key of BINDER_PARAM_KEYS) {
      const all = query.getAll(key)
      if (all.length === 0) continue
      if (all.length > 1) dirty = true
      const raw = all[0]
      let ok = false
      if (key === PAGE) {
        // '?page=007' keeps page 7 (only the spelling is redone); page 1,
        // 0, negatives and non-numbers fall back to the default.
        const n = positiveInt(raw)
        if (n !== null && n > 1) { params.page = n; ok = String(n) === raw }
      } else if (key === Q) {
        if (raw !== '') { params.q = raw; ok = true }
      } else if (key === LAYOUT) {
        if (LAYOUTS.has(raw)) { params.layout = raw as BinderLayout; ok = true }
      } else if (key === GROUP) {
        if (GROUPS.has(raw)) { params.group = raw as GroupMode; ok = true }
      } else if (SORT_IDS.has(raw)) {
        params.sort = raw as SortOptionId
        ok = true
      }
      if (!ok) dirty = true
    }
  } else if (BINDER_PARAM_KEYS.some(k => query.has(k))) {
    dirty = true
  }

  const out: CollectionLocation = { section: path.section, params }
  if (path.binder) out.binder = path.binder
  if (dirty) {
    const base = collectionPath(path.section, path.binder, params)
    const [p, qs = ''] = base.split('?')
    const merged = new URLSearchParams(qs)
    for (const [k, v] of query) if (!owned.has(k)) merged.append(k, v)
    const s = merged.toString()
    out.normalized = s ? `${p}?${s}` : p
  }
  return out
}
